<script setup lang="ts">
import { AxiosError, isAxiosError } from 'axios'
import Debug from 'debug'
import { McdViewError } from '~/lib/models/errors'
import { GTMContentGroup } from '~/lib/models/gtm'
const props = defineProps<{
  error: AxiosError | McdViewError | Error
}>()

const debug = Debug('app:view:error')
const { t } = useLocale()
const { trackPageView } = useGTM()

const statusCode = ref(404)
const redirectPath = ref<string>()

const error = ref(props.error)
if (isAxiosError(error.value)) {
  debug('Axios Error message: ', error.value.message)
  debug('Axios Error Data: ', error.value.response)
} else if (error.value instanceof McdViewError) {
  const errorInfo = error.value.getErrorInfo()
  statusCode.value = errorInfo.statusCode
  redirectPath.value = errorInfo.redirectPath
  debug('McdViewError Error message: ', error.value.message)
  debug('McdViewError Error Data: ', errorInfo)
} else {
  debug('Error message: ', error.value.message)
  debug('Error Data: ', error.value)
  if (error.value.stack) debug('Error stack', error.value.stack)
}

if (redirectPath.value) {
  navigateTo(redirectPath.value, { redirectCode: 301 })
} else if (process.server) {
  const event = useRequestEvent()
  if (event) setResponseStatus(event, statusCode.value)
}

useHeadSafe({
  meta: [
    {
      name: 'robots',
      content: 'noindex, follow',
    },
  ],
})

const clear = () => {
  const redirect = redirectPath.value || '/'
  clearError({ redirect })
}

onMounted(() => {
  trackPageView({
    cg: GTMContentGroup.primary.ERROR,
    cg2: statusCode.value.toString(),
  })
})
</script>

<template>
  <main
    class="mx-auto flex min-h-[80svh] flex-col items-center justify-center gap-5 p-5 md:grid md:min-h-[75svh] md:w-[100%] md:grid-cols-[40%_1fr_55%] md:grid-rows-1 md:place-content-center md:gap-0 lg:w-[75%]"
  >
    <section class="flex w-full flex-col md:col-span-1">
      <img class="flex h-auto w-9/12 self-center" src="/images/error/error.jpg" alt="error" />
    </section>

    <section class="flex w-full flex-col md:col-start-3 md:col-end-3">
      <div class="my-2 md:mt-10">
        <h2 class="text-start text-2xl font-bold md:text-6xl">
          {{ t('errors.page.title') }}
        </h2>
        <h2 class="mt-1 text-start text-2xl font-bold md:text-6xl">
          {{ t('errors.page.subtitle') }}
        </h2>
      </div>

      <p class="my-4 text-sm text-mcd-secondaryDarkGrey md:text-base">
        {{ t('errors.page.description') }}
      </p>

      <McdButton is-primary class="w-fit px-4 py-3 md:px-10 md:py-1" @on-click="clear">
        Volver
      </McdButton>
    </section>
  </main>
</template>
